import { useEffect, useState } from "react";
import { useAuth } from "../App";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import ActivityTrailMap from "./ActivityTrailMap";
import DualViewSwitcher from "./DualViewSwitcher";
import { getAccessTier, TIER_LABELS } from "../lib/roleAccess";
import {
  Radio, CalendarClock, TrendingUp, Trophy, Users, Clock, UserX,
  Loader2, LogOut, MapPin, Crown, Gift,
} from "lucide-react";

const money = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

function HorizonHeader({ icon: Icon, tone, kicker, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{kicker}</div>
        <h2 className="font-heading font-bold text-slate-900 text-lg leading-tight">{title}</h2>
      </div>
    </div>
  );
}

function StatPill({ label, value, tone = "slate", testid }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${map[tone]}`} data-testid={testid}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="text-xs mt-1">{label}</div>
    </div>
  );
}

const ShiftRow = ({ s }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm" data-testid="tl-shift-row">
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-xs text-slate-400 w-12 shrink-0">{s.date?.slice(5)}</span>
      <Clock className="h-3 w-3 text-slate-300 shrink-0" />
      <span className="text-xs text-slate-500 w-20 shrink-0">{s.startTime}-{s.endTime}</span>
      <span className="truncate text-slate-700">{s.locationName || "—"}</span>
    </div>
    {s.isUnassigned
      ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 gap-1 shrink-0"><UserX className="h-3 w-3" />Unassigned</Badge>
      : <span className="text-xs text-slate-600 truncate max-w-[45%] text-right">{(s.userNames || []).join(", ")}</span>}
  </div>
);

export default function TeamLeaderDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tier = getAccessTier(user);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await apiClient.get("/team-leader/home");
        setData(r.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ts = data?.teamSummary || {};
  const members = data?.members || [];
  const thisWeek = data?.schedule?.thisWeek || [];
  const nextWeek = data?.schedule?.nextWeek || [];
  const coverageGaps = data?.schedule?.coverageGaps || [];
  const leaderboard = data?.leaderboard || [];
  const myRank = leaderboard.find((t) => t.isMine)?.rank;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="team-leader-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-slate-900 leading-tight">Team Command</div>
              <div className="text-xs text-slate-500">
                {user?.name} · <span className="font-medium text-indigo-600">{TIER_LABELS[tier] || tier}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DualViewSwitcher />
            <Button variant="ghost" size="sm" onClick={logout} data-testid="tl-logout" className="gap-1.5 text-slate-500">
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-12 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading your team…
          </div>
        ) : (
          <>
            {/* A. LIVE NOW — team heat map */}
            <section data-testid="tl-now">
              <HorizonHeader icon={Radio} tone="bg-rose-100 text-rose-600" kicker="Live now" title="Your team in the field" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatPill label="Team members" value={ts.memberCount ?? 0} tone="indigo" testid="tl-member-count" />
                <StatPill label="Active members" value={ts.activeMembers ?? 0} tone="emerald" testid="tl-active-members" />
                <StatPill label="Shifts this week" value={thisWeek.length} tone="slate" testid="tl-shifts-week" />
                <StatPill label="Coverage gaps" value={coverageGaps.length} tone={coverageGaps.length ? "rose" : "slate"} testid="tl-coverage-gaps" />
              </div>
              <Card className="border-slate-200">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700">
                    <MapPin className="h-4 w-4 text-rose-500" /> Team activity map (your members only)
                  </div>
                  <ActivityTrailMap />
                </CardContent>
              </Card>
            </section>

            {/* B. WHAT'S NEXT — team schedule */}
            <section data-testid="tl-next">
              <HorizonHeader icon={CalendarClock} tone="bg-indigo-100 text-indigo-600" kicker="What's next" title="Your team's schedule" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border-slate-200" data-testid="tl-this-week">
                  <CardContent className="p-4">
                    <div className="font-medium text-slate-800 mb-2">This week <span className="text-xs text-slate-400">({thisWeek.length} shifts)</span></div>
                    <div className="max-h-56 overflow-y-auto">
                      {thisWeek.length ? thisWeek.slice(0, 30).map((s) => <ShiftRow key={s.id} s={s} />)
                        : <p className="text-sm text-slate-400 py-4 text-center">No shifts scheduled this week.</p>}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="tl-next-week">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-800">Next week <span className="text-xs text-slate-400">({nextWeek.length} shifts)</span></span>
                      {coverageGaps.length > 0 && (
                        <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">{coverageGaps.length} gap{coverageGaps.length === 1 ? "" : "s"}</Badge>
                      )}
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {nextWeek.length ? nextWeek.slice(0, 30).map((s) => <ShiftRow key={s.id} s={s} />)
                        : <p className="text-sm text-slate-400 py-4 text-center">No shifts scheduled next week.</p>}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* C. TEAM PERFORMANCE — totals + gift aid % per fundraiser */}
            <section data-testid="tl-performance">
              <HorizonHeader icon={TrendingUp} tone="bg-emerald-100 text-emerald-600" kicker="To date" title="Team performance & Gift Aid" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatPill label="Total raised (team)" value={money(ts.totalRaised)} tone="emerald" testid="tl-total-raised" />
                <StatPill label="Net sign-ups" value={ts.netSignups ?? 0} tone="indigo" testid="tl-net-signups" />
                <StatPill label="Gift Aid %" value={`${ts.giftAidPct ?? 0}%`} tone="violet" testid="tl-giftaid-pct" />
                <StatPill label="Cancelled" value={ts.cancelled ?? 0} tone={ts.cancelled ? "rose" : "slate"} testid="tl-cancelled" />
              </div>
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tl-member-table">
                      <thead>
                        <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                          <th className="px-4 py-2.5 font-medium">Fundraiser</th>
                          <th className="px-4 py-2.5 font-medium text-right">Sign-ups</th>
                          <th className="px-4 py-2.5 font-medium text-right">Net</th>
                          <th className="px-4 py-2.5 font-medium text-right">Raised</th>
                          <th className="px-4 py-2.5 font-medium text-right"><Gift className="h-3.5 w-3.5 inline -mt-0.5" /> Gift Aid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.length ? members.map((m) => (
                          <tr key={m.userId} className="border-b border-slate-50 last:border-0" data-testid="tl-member-row">
                            <td className="px-4 py-2.5 text-slate-700">{m.name}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{m.signups}</td>
                            <td className="px-4 py-2.5 text-right font-medium text-slate-800">{m.netSignups}</td>
                            <td className="px-4 py-2.5 text-right text-emerald-700 font-medium">{money(m.raised)}</td>
                            <td className="px-4 py-2.5 text-right text-violet-700">{m.giftAidPct}%</td>
                          </tr>
                        )) : (
                          <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No team activity yet.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* D. CAMPAIGN LEADERBOARD — all teams (aggregate) */}
            <section data-testid="tl-leaderboard">
              <HorizonHeader icon={Trophy} tone="bg-amber-100 text-amber-600" kicker="Leaderboard"
                title={`Teams on your campaign${myRank ? ` · you're #${myRank}` : ""}`} />
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tl-leaderboard-table">
                      <thead>
                        <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                          <th className="px-4 py-2.5 font-medium">#</th>
                          <th className="px-4 py-2.5 font-medium">Team</th>
                          <th className="px-4 py-2.5 font-medium text-right">Members</th>
                          <th className="px-4 py-2.5 font-medium text-right">Net sign-ups</th>
                          <th className="px-4 py-2.5 font-medium text-right">Raised</th>
                          <th className="px-4 py-2.5 font-medium text-right">Avg Gift Aid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaderboard.length ? leaderboard.map((t) => (
                          <tr key={t.teamKey}
                              className={`border-b border-slate-50 last:border-0 ${t.isMine ? "bg-indigo-50/60" : ""}`}
                              data-testid="tl-leaderboard-row">
                            <td className="px-4 py-2.5 text-slate-500">
                              {t.rank === 1 ? <Crown className="h-4 w-4 text-amber-500 inline" /> : t.rank}
                            </td>
                            <td className="px-4 py-2.5 text-slate-700 font-medium">
                              {t.teamName}
                              {t.isMine && <Badge className="ml-2 bg-indigo-600 text-white text-[10px]">You</Badge>}
                            </td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{t.memberCount}</td>
                            <td className="px-4 py-2.5 text-right font-medium text-slate-800">{t.netSignups}</td>
                            <td className="px-4 py-2.5 text-right text-emerald-700 font-medium">{money(t.totalRaised)}</td>
                            <td className="px-4 py-2.5 text-right text-violet-700">{t.giftAidPct}%</td>
                          </tr>
                        )) : (
                          <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No teams to rank yet.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
